import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchAttempts, fetchFailureReasonCodes, fetchTasks } from '../api/client';
import type { DeliveryAttempt } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { breakdownByReason, countByStatus, isSuccessfulAttempt } from '../lib/delivery-board';
import { formatTaskStatus } from '../lib/task-actions';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Papan monitoring pengiriman.
 *
 * SENGAJA tanpa peta real-time (ADR-0036 §D1). Yang dijawab papan ini bukan "di mana kurir
 * sekarang" melainkan "berapa yang macet, dan karena apa" — pertanyaan yang menghasilkan
 * tindakan dispatcher, dan yang tidak menuntut pelacakan berkelanjutan atas orang.
 */
export function DeliveryBoardPage(): ReactNode {
  const session = useSession();

  const { state, reload } = useAsync(async () => {
    const [tasks, codes] = await Promise.all([
      fetchTasks(session.auth),
      fetchFailureReasonCodes(session.auth),
    ]);

    // Attempt hanya ditarik untuk tugas yang GAGAL. Menarik seluruh attempt setiap muat akan
    // membebani papan dengan data yang tidak dipakai sebaran alasan.
    const failed = tasks.filter((t) => t.status === 'failed');
    const attemptLists = await Promise.all(
      failed.map((t) =>
        fetchAttempts(session.auth, t.id).catch((): readonly DeliveryAttempt[] => []),
      ),
    );

    return { tasks, codes, attempts: attemptLists.flat() };
  }, [session.auth]);

  if (state.status === 'loading') return <LoadingState label="Memuat papan pengiriman…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const { tasks, codes, attempts } = state.data;
  const counts = countByStatus(tasks);
  const failedAttempts = attempts.filter((a) => !isSuccessfulAttempt(a.outcome));
  const breakdown = breakdownByReason(failedAttempts, codes);

  return (
    <>
      <section className="card">
        <h2>Pantauan pengiriman</h2>
        <p className="muted small">
          Ringkasan status dan sebab kegagalan. Workforce tidak melacak posisi kurir secara
          terus-menerus — yang tercatat hanya titik pada tiap transisi.
        </p>
      </section>

      <section className="card">
        <h3>Status</h3>
        <div className="board">
          {counts.map((c) => (
            <div key={c.status} className="board-cell">
              <span className="board-count">{c.count}</span>
              <span className="muted small">{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h3>Sebaran alasan gagal</h3>
        {breakdown.length === 0 ? (
          <p className="muted small">Belum ada kegagalan tercatat.</p>
        ) : (
          <table className="compare">
            <thead>
              <tr>
                <th scope="col">Alasan</th>
                <th scope="col">Jumlah</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row) => (
                <tr key={row.reasonCodeId ?? 'none'}>
                  <th scope="row">{row.label}</th>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h3>Tugas gagal</h3>
        {tasks.filter((t) => t.status === 'failed').length === 0 ? (
          <EmptyState title="Tidak ada tugas gagal" hint="Semua tugas berjalan tanpa kegagalan." />
        ) : (
          <ul className="list">
            {tasks
              .filter((t) => t.status === 'failed')
              .map((task) => (
                <li key={task.id} className="list-item">
                  <Link to={`/tasks/${task.id}`}>
                    <strong>{task.title}</strong>
                  </Link>
                  <p className="muted small">
                    {task.taskNumber} · {formatTaskStatus(task.status)}
                  </p>
                </li>
              ))}
          </ul>
        )}
      </section>
    </>
  );
}
